/**
 * "Annotation Versions" panel mounted under the Queue page — per
 * `feature-spec.md` §3.3 (prototype-faithful: the snapshot history lives on
 * the Queue, not on a separate dashboard).
 *
 * Lists snapshots newest-first via `useSnapshotPages` (B7), with a "Save
 * snapshot" CTA in the header (opens `SaveSnapshotModal`) and a per-row
 * Rollback button (opens `RollbackConfirmDialog`). The active snapshot
 * gets an "Active" indicator and its Rollback button is disabled.
 */
import React, { useState } from 'react';

import { Badge } from '../../../../../ui/badge';
import { Button } from '../../../../../ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../../ui/card';
import { formatTimeAgo } from '../../../../../../utils/dateUtils';
import { useSnapshotPages } from '../../hooks/useSnapshots';
import type { Snapshot, SnapshotList } from '../../types';
import RollbackConfirmDialog from './RollbackConfirmDialog';
import SaveSnapshotModal from './SaveSnapshotModal';

// `PAGE_SIZE` matches the BE list endpoint's per-page max so a single
// network round-trip exhausts a page. "Load more" reveals another page;
// `useSnapshotPages` observes every revealed page at once and the items are
// flattened into the visible list — keeps pagination out of the query string
// while still letting the panel scale past the first 50 snapshots.
const PAGE_SIZE = 50;
const NOTES_PREVIEW_LIMIT = 200;

const STATUS_LABEL: Record<Snapshot['status'], string> = {
  active: 'Active',
  archived: 'Archived',
  'rolled-back': 'Rolled back',
};

/**
 * Flatten the loaded snapshot pages into one newest-first list, de-duped by
 * id — a freshly-inserted snapshot can shift items across a page boundary, so
 * the same row may appear on two pages. Exported for unit testing.
 */
export const flattenSnapshotPages = (
  pages: Array<SnapshotList | undefined>,
): Snapshot[] => {
  const seen = new Set<string>();
  const out: Snapshot[] = [];
  for (const page of pages) {
    for (const snap of page?.items ?? []) {
      if (!seen.has(snap.id)) {
        seen.add(snap.id);
        out.push(snap);
      }
    }
  }
  return out;
};

const SnapshotHistoryPanel: React.FC = () => {
  // Each "Load more" reveals another page. `useSnapshotPages` observes every
  // revealed page at once, so a save/rollback that invalidates the snapshot
  // tree refetches all of them — not just the page in view — and the fresh
  // active snapshot / status changes surface without a remount.
  const [pageCount, setPageCount] = useState(1);
  const { pages, total, isLoading, isError, isFetching, refetch } =
    useSnapshotPages(pageCount, PAGE_SIZE);
  const accumulated = flattenSnapshotPages(pages);
  const hasMore = accumulated.length < total;

  const [saveOpen, setSaveOpen] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<Snapshot | null>(null);
  // Notes longer than NOTES_PREVIEW_LIMIT are truncated by default
  // with a "Show more" toggle. A `title=` hover tooltip works on
  // desktop but is invisible on mobile; this gives both surfaces a
  // click affordance. Set keys = snapshot ids that are currently
  // expanded.
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleNotesExpanded = (id: string) =>
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Card data-testid="snapshot-history-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Annotation Versions</CardTitle>
        <Button
          variant="secondary"
          size="sm"
          className="h-8 rounded-md border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-xs hover:bg-accent"
          onClick={() => setSaveOpen(true)}
          data-testid="snapshot-save-button"
        >
          Save snapshot
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2" data-testid="snapshot-loading">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-md border border-border/70 bg-card"
              />
            ))}
          </div>
        ) : isError ? (
          <div
            className="flex h-32 flex-col items-center justify-center text-center"
            data-testid="snapshot-error"
          >
            <p className="mb-2 text-muted-foreground">Failed to load snapshots.</p>
            <Button variant="link" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : accumulated.length === 0 ? (
          <p
            className="py-6 text-center text-sm text-muted-foreground"
            data-testid="snapshot-empty-state"
          >
            No snapshots yet. Capture one to mark this annotation state as a
            recoverable baseline.
          </p>
        ) : (
          <ul className="space-y-3">
            {accumulated.map((snap) => {
              const isActive = snap.status === 'active';
              return (
                <li
                  key={snap.id}
                  className="flex items-start justify-between gap-4 rounded-md border border-border bg-background p-3"
                  data-testid={`snapshot-row-${snap.id}`}
                  data-active={isActive}
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[12px] text-muted-foreground">
                        {snap.version}
                      </span>
                      <span className="text-[13px] font-semibold text-foreground">
                        {snap.label}
                      </span>
                      <Badge
                        variant={
                          isActive
                            ? 'success'
                            : snap.status === 'rolled-back'
                            ? 'destructive'
                            : 'secondary'
                        }
                        data-testid={`snapshot-status-${snap.id}`}
                      >
                        {STATUS_LABEL[snap.status]}
                      </Badge>
                    </div>
                    <div className="text-[12px] text-muted-foreground">
                      {snap.stats.totalRecords} records ·{' '}
                      {snap.stats.finalised} finalised ·{' '}
                      {snap.stats.disputed} disputed ·{' '}
                      {formatTimeAgo(snap.createdAt)}
                    </div>
                    {snap.notes && (() => {
                      const isLong = snap.notes.length > NOTES_PREVIEW_LIMIT;
                      const isExpanded = expandedNotes.has(snap.id);
                      const shown =
                        !isLong || isExpanded
                          ? snap.notes
                          : `${snap.notes.slice(0, NOTES_PREVIEW_LIMIT)}…`;
                      return (
                        <p
                          className="text-[12px] italic text-muted-foreground"
                          data-testid={`snapshot-notes-${snap.id}`}
                        >
                          {shown}
                          {isLong && (
                            <>
                              {' '}
                              <button
                                type="button"
                                onClick={() => toggleNotesExpanded(snap.id)}
                                className="not-italic font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                                data-testid={`snapshot-notes-toggle-${snap.id}`}
                                aria-expanded={isExpanded}
                              >
                                {isExpanded ? 'Show less' : 'Show more'}
                              </button>
                            </>
                          )}
                        </p>
                      );
                    })()}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRollbackTarget(snap)}
                    disabled={isActive}
                    data-testid={`rollback-${snap.id}`}
                  >
                    Rollback
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        {hasMore && !isLoading && !isError && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPageCount((n) => n + 1)}
              disabled={isFetching}
              data-testid="snapshot-load-more"
            >
              {isFetching
                ? 'Loading…'
                : `Load ${Math.min(
                    PAGE_SIZE,
                    total - accumulated.length,
                  )} more`}
            </Button>
          </div>
        )}
      </CardContent>

      <SaveSnapshotModal
        open={saveOpen}
        onOpenChange={setSaveOpen}
      />
      <RollbackConfirmDialog
        open={rollbackTarget !== null}
        onOpenChange={(o) => {
          if (!o) setRollbackTarget(null);
        }}
        snapshot={rollbackTarget}
      />
    </Card>
  );
};

export default SnapshotHistoryPanel;
